import { ComponentProps, ElementType } from 'react';
import { styled, css } from 'styled-components';
import { ColorName } from '../../styles/colors.ts';

export type DesktopTextProps<T extends ElementType = 'p'> = {
  variant?: Variant;
  uppercase?: boolean;
  color?: ColorName;
  align?: 'left' | 'center' | 'right';
  weight?: number;
} & AsProp<T>;

const DesktopText = <T extends ElementType = 'p'>({
  asTag,
  variant,
  uppercase,
  color,
  align,
  weight,
  ...props
}: DesktopTextProps<T>) => (
  <Wrapper
    as={asTag}
    $variant={variant}
    $uppercase={uppercase}
    $color={color}
    $align={align}
    $weight={weight}
    {...props}
  />
);

type AsProp<T extends ElementType> = {
  asTag?: T;
} & ComponentProps<T>;

type Variant =
  | 'h1'
  // Deprecated?
  | 'h1-round'
  | 'h2-middle'
  // Same as h2-middle?
  | 'h2-side-time'
  // Same as h2-middle?
  | 'h2-center'
  | 'h3'
  | 'h3-prizepool'
  // Same as h3?
  | 'h3-center-no bets'
  | 'h4'
  | 'h5'
  // Same as h5?
  | 'h5-elements'
  | 'text-help';

const stylesByVariant: {
  [key in Variant]: ReturnType<typeof css>;
} = {
  h1: css`
    font-size: ${({ theme }) => theme.responsive(36)};
    line-height: ${36 / 36};
    font-weight: 600;
    letter-spacing: -0.72px;
  `,
  // Probably is not going to be used and can be deprecated
  'h1-round': css`
    font-size: 24px;
    line-height: ${28 / 24};
    font-weight: 400;
  `,
  'h2-middle': css`
    font-size: ${({ theme }) => theme.responsive(32)};
    line-height: ${32 / 36};
    font-weight: 600;
    letter-spacing: -0.64px;
  `,
  // Seems like same styles as h2-side-time
  'h2-side-time': css`
    ${() => stylesByVariant['h2-middle']}
  `,
  // Seems like the same again, the only difference 42 line-height
  'h2-center': css`
    ${() => stylesByVariant['h2-middle']}
  `,
  h3: css`
    font-size: ${({ theme }) => theme.responsive(28)};
    line-height: ${36 / 28};
    font-weight: 600;
  `,
  'h3-prizepool': css`
    font-size: ${({ theme }) => theme.responsive(21)};
    line-height: ${36 / 21};
    font-weight: 600;
    color: ${({ theme }) => theme.getColor('active')};
  `,
  // Seems like the same again, the only difference 32 line-height
  'h3-center-no bets': css`
    ${() => stylesByVariant['h3']};
  `,
  h4: css`
    font-size: ${({ theme }) => theme.responsive(24)};
    line-height: ${36 / 24};
    font-weight: 600;
    color: ${({ theme }) => theme.getColor('accentPrimary')};
  `,
  h5: css`
    font-size: ${({ theme }) => theme.responsive(21)};
    // In design was 32
    line-height: ${24 / 21};
    font-weight: 600;
    color: ${({ theme }) => theme.getColor('textDark')};
  `,
  /**
   * Seems like the same again, the only difference 24 line-height
   * instead of 32 above. Will set 24 upper and use h5 only */
  'h5-elements': css`
    ${() => stylesByVariant['h5']};
  `,
  'text-help': css`
    font-size: ${({ theme }) => theme.responsive(18)};
    line-height: ${21 / 18};
    letter-spacing: -0.36px;
  `,
};

// noinspection CssUnusedSymbol,DuplicatedCode
const Wrapper = styled.p<{
  $variant?: Variant;
  $uppercase?: boolean;
  $color?: ColorName;
  $align?: 'left' | 'center' | 'right';
  $weight?: number;
}>`
  letter-spacing: -0.32px;

  ${({ $variant }) => $variant && stylesByVariant[$variant]};

  ${({ $uppercase }) =>
    $uppercase &&
    css`
      text-transform: uppercase;
    `}

  ${({ $color }) =>
    $color &&
    css`
      color: ${({ theme }) => theme.getColor($color)};
    `}

  ${({ $align }) =>
    $align &&
    css`
      text-align: ${$align};
    `}

  ${({ $weight }) =>
    $weight &&
    css`
      font-weight: ${$weight};
    `}

  & span.accent {
    color: ${({ theme }) => theme.getColor('accentSecondary')};
  }
`;

export { DesktopText };
